/**
 * PrimeReact 7.2 `scrollable` TabViews start with `forwardIsDisabled: false`
 * and only re-check after the user scrolls the tab bar, so the "next" arrow
 * shows even when every tab fits. This watcher marks each TabView with
 * `data-tabs-overflow="true" | "false"` (re-checked on resize and whenever
 * tabs are added/changed); TabViewOverflow.css hides the arrows on tab bars
 * that fit, so they only appear when the tabs actually need scrolling
 * (typically on mobile / narrow screens).
 */
const observed = new WeakSet<Element>();
let scheduled = false;

function updateOverflow(content: HTMLElement): void {
  const tabview = content.closest('.p-tabview');
  if (!tabview) {
    return;
  }
  const overflowing = content.scrollWidth - content.clientWidth > 1;
  const value = overflowing ? 'true' : 'false';
  if (tabview.getAttribute('data-tabs-overflow') !== value) {
    tabview.setAttribute('data-tabs-overflow', value);
  }
}

function navContentOf(el: Element): HTMLElement | null {
  return el.classList.contains('p-tabview-nav-content')
    ? (el as HTMLElement)
    : el.closest<HTMLElement>('.p-tabview-nav-content');
}

export function initTabViewOverflowWatcher(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  const w = window as any;
  if (w.__tabViewOverflowWatcher) {
    return;
  }
  w.__tabViewOverflowWatcher = true;

  // "ResizeObserver loop completed with undelivered notifications" is a benign
  // browser notice (the remaining notifications are delivered next frame), but
  // the CRA dev overlay reports it as an uncaught error. Swallow only that one
  // message, in the capture phase so it runs before the overlay's listener.
  window.addEventListener(
    'error',
    (event: ErrorEvent) => {
      if (event.message && event.message.indexOf('ResizeObserver loop') !== -1) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    },
    true
  );

  // Changing data-tabs-overflow shows/hides the arrows, which resizes the
  // observed elements. Doing that inside the ResizeObserver callback triggers
  // "ResizeObserver loop completed with undelivered notifications", so the
  // updates are collected and applied in the next animation frame instead.
  const pending = new Set<HTMLElement>();
  let pendingFrame = false;
  const flushPending = () => {
    pendingFrame = false;
    pending.forEach((content) => updateOverflow(content));
    pending.clear();
  };

  const resizeObserver: ResizeObserver | null =
    typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver((entries) => {
          entries.forEach((entry) => {
            const content = navContentOf(entry.target);
            if (content) {
              pending.add(content);
            }
          });
          if (pending.size > 0 && !pendingFrame) {
            pendingFrame = true;
            window.requestAnimationFrame(flushPending);
          }
        })
      : null;

  const scan = () => {
    scheduled = false;
    document.querySelectorAll<HTMLElement>('.p-tabview-nav-content').forEach((content) => {
      if (!observed.has(content)) {
        observed.add(content);
        if (resizeObserver) {
          resizeObserver.observe(content);
          const nav = content.querySelector('.p-tabview-nav');
          if (nav) {
            resizeObserver.observe(nav);
          }
        }
      }
      updateOverflow(content);
    });
  };

  const scheduleScan = () => {
    if (!scheduled) {
      scheduled = true;
      window.requestAnimationFrame(scan);
    }
  };

  new MutationObserver(scheduleScan).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('resize', scheduleScan);
  scheduleScan();
}
